import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";
import {markMinorConversions} from "@repo/analytics-learning-time";
import {cssUrl} from "@repo/urls";

import {LastChanceCollageHeight} from "#marketing-content-shared-package/collages/last-chance.tsx";
import * as mq from "#marketing-content-shared-package/media-queries.ts";
import {SerifHeadingMedium} from "#marketing-content-shared-package/typography/typography.ts";
import DonateButton from "#page-package/donate-button.tsx";

type Props = {
    kind: "LastChance";
    showDistrictsCTA?: boolean;
    localeIsEnglish?: boolean;
};

export default class LastChance extends React.Component<Props> {
    scrollToTop: () => void = () => {
        window.scrollTo(0, 0);
    };

    render(): React.ReactNode {
        const {showDistrictsCTA, localeIsEnglish} = this.props;

        const donateCtaCopy = localeIsEnglish ? t`Give today` : t`Donate`;

        // sync-start:join-text 1209997083 services/static/dev/cypress/e2e/integration/set-locale.spec.ts
        // Make sure to update the above spec if this text changes.
        const joinText = t`Join Khan Academy today`;
        // sync-end:join-text

        return (
            <View id="last-chance-lohp" style={styles.container} tabIndex={-1}>
                <SerifHeadingMedium tag="h2" style={styles.header}>
                    {joinText}
                </SerifHeadingMedium>
                <View style={styles.content}>
                    <ul className={css(styles.buttonContainer)}>
                        <li>
                            <Button
                                href="/signup"
                                size="large"
                                safeWithNav={() =>
                                    markMinorConversions([
                                        {
                                            id: "logged_out_start_learning_click",
                                        },
                                    ])
                                }
                                style={styles.cta}
                            >
                                {t`Learners`}
                            </Button>
                        </li>
                        <li>
                            <Button
                                href="/signup?isteacher=1"
                                size="large"
                                safeWithNav={() =>
                                    markMinorConversions([
                                        {
                                            id: "logged_out_teacher_start_click",
                                        },
                                    ])
                                }
                                style={styles.cta}
                            >
                                {t`Teachers`}
                            </Button>
                        </li>
                        {showDistrictsCTA && (
                            <li>
                                <Button
                                    href="https://districts.khanacademy.org/"
                                    size="large"
                                    safeWithNav={() =>
                                        markMinorConversions([
                                            {
                                                id: "logged_out_districts_start_click",
                                            },
                                        ])
                                    }
                                    style={styles.cta}
                                >
                                    {t`Districts`}
                                </Button>
                            </li>
                        )}
                        <li>
                            <Button
                                href="/signup?isparent=1"
                                size="large"
                                safeWithNav={() =>
                                    markMinorConversions([
                                        {
                                            id: "logged_out_parent_start_click",
                                        },
                                    ])
                                }
                                style={styles.cta}
                            >
                                {t`Parents`}
                            </Button>
                        </li>
                        <li>
                            <DonateButton
                                style={[styles.cta, styles.donateCta]}
                                urlParams={{
                                    c_src2: "Webapp.lohplastchance",
                                }}
                                size="large"
                                safeWithNav={() =>
                                    markMinorConversions([
                                        {
                                            id: "logged_out_donors_start_click",
                                        },
                                    ])
                                }
                            >
                                {donateCtaCopy}
                            </DonateButton>
                        </li>
                        <li>
                            <Button
                                kind="secondary"
                                onClick={this.scrollToTop}
                                style={[styles.cta, styles.backButton]}
                            >
                                {t`Back`}
                            </Button>
                        </li>
                    </ul>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
        margin: "0 0 166px 0",
        padding: 0,
        [mq.smallDesktopOrSmaller]: {
            justifyContent: "center",
            minHeight: "100vh",
            paddingTop: 0,
            paddingBottom: 0,
        },
        ":focus": {
            outline: 0,
        },
    },
    header: {
        textAlign: "center",
        [mq.largePhoneOrSmaller]: {
            paddingLeft: 50,
            paddingRight: 50,
        },
    },
    content: {
        position: "relative",
        width: "100%",
        margin: "24px 0 0 0",
        padding: "24px 0 24px 0",
        minHeight: LastChanceCollageHeight,
        alignItems: "center",
        backgroundImage: cssUrl("/images/lohp/last_chance_collage@2x.png"),
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundPosition: "center",
        [mq.smallDesktopOrSmaller]: {
            width: "100%",
            marginTop: 32,
            paddingTop: 32,
        },
        [mq.largePhoneOrSmaller]: {
            marginTop: 16,
            paddingTop: 16,
        },
    },
    buttonContainer: {
        alignItems: "center",
        width: "90%",
        maxWidth: 335,
    },
    cta: {
        margin: "0 0 8px 0",
        width: "100%",
    },
    donateCta: {
        // we intentionally hide this on tablet/mobile
        [mq.tabletOrSmaller]: {
            display: "none",
        },
    },
    backButton: {
        // NOTE: Overrides the bg to emulate a light surface
        background: semanticColor.surface.primary,
        ":hover": {
            background: semanticColor.surface.primary,
        },
        // we intentionally hide this on desktop
        [mq.smallDesktopOrLarger]: {
            display: "none",
        },
    },
});
